import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, ChevronDown, Clock, Download, FileText, LogOut, UploadCloud } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import SEO from "@/components/SEO";

// Mirrors APP_STATUS in AdminDashboard: what staff set is what the client sees.
const APP_STATUS = {
  received: { label: "Received", className: "bg-slate-100 text-slate-600 border-slate-200" },
  in_progress: { label: "In Progress", className: "bg-gold-500/10 text-gold-700 border-gold-300/50" },
  completed: { label: "Completed", className: "bg-emerald2-500/10 text-emerald2-700 border-emerald2-300/40" },
  rejected: { label: "Rejected", className: "bg-red-50 text-red-600 border-red-200" },
};

const STATUS_STYLE = {
  pending: "bg-gold-500/10 text-gold-700 border-gold-300/50",
  reviewed: "bg-emerald2-500/10 text-emerald2-700 border-emerald2-300/40",
};

const MAX_REQUIRED_DOCS = 10;

const requiredLabelsFor = (serviceIndex, slug) => {
  const service = serviceIndex.find((s) => s.slug === slug);
  if (!service?.documents) return [];
  return Array.from(new Set(service.documents.groups.flatMap((g) => g.items))).slice(0, MAX_REQUIRED_DOCS);
};

const DocumentRow = ({ doc, token, expanded, onToggle }) => (
  <div className="border-b border-slate-100 last:border-b-0" data-testid={`account-doc-row-${doc.id}`}>
    <button
      type="button"
      onClick={onToggle}
      data-testid={`account-doc-toggle-${doc.id}`}
      className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors hover:bg-[rgb(238,242,250)]/50"
    >
      <span className="flex min-w-0 items-center gap-2 font-medium text-navy-800">
        <FileText className="h-4 w-4 shrink-0 text-emerald2-600" />
        <span className="truncate">{doc.document_label}</span>
      </span>
      <span className="flex shrink-0 items-center gap-4 text-sm">
        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${STATUS_STYLE[doc.status]}`}>
          {doc.status}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </span>
    </button>
    {expanded && (
      <div className="grid grid-cols-1 gap-3 border-t border-slate-100 bg-[rgb(238,242,250)]/40 px-5 py-4 text-sm sm:grid-cols-2" data-testid={`account-doc-detail-${doc.id}`}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">File Name</p>
          <p className="mt-0.5 text-navy-800">{doc.file_name}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Uploaded</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-navy-800">
            <Clock className="h-3.5 w-3.5" /> {new Date(doc.uploaded_at).toLocaleString()}
          </p>
        </div>
        <div className="sm:col-span-2">
          <button
            type="button"
            onClick={() => api.downloadDocument(token, doc.id, doc.file_name).catch((err) => toast.error(err.message))}
            data-testid={`account-doc-download-${doc.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-navy-900/15 bg-white px-3.5 py-1.5 text-xs font-semibold text-navy-800 transition-colors hover:border-gold-400 hover:text-gold-600"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </button>
        </div>
      </div>
    )}
  </div>
);

export default function Account() {
  const { user, token, logout } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [serviceIndex, setServiceIndex] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    Promise.all([api.myDocuments(token), api.listServices(), api.myApplications(token)])
      .then(([docsRes, servicesRes, appsRes]) => {
        setDocuments(docsRes.documents);
        setServiceIndex(servicesRes.services.map((s) => ({ slug: s.slug, documents: s.sections?.documents || null })));
        setApplications(appsRes.applications || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const serviceGroups = useMemo(() => {
    const statusOf = new Map(applications.map((a) => [a.service_slug, a]));
    const bySlug = new Map();
    documents.forEach((d) => {
      if (!bySlug.has(d.service_slug)) {
        bySlug.set(d.service_slug, { slug: d.service_slug, category: d.service_category, title: d.service_title, docs: [] });
      }
      bySlug.get(d.service_slug).docs.push(d);
    });
    return Array.from(bySlug.values())
      .map((group) => {
        const required = requiredLabelsFor(serviceIndex, group.slug);
        const uploadedLabels = new Set(group.docs.map((d) => d.document_label));
        const missing = required.filter((label) => !uploadedLabels.has(label));
        const app = statusOf.get(group.slug);
        return { ...group, missing, status: app?.status || "received", statusNote: app?.note || "" };
      })
      .sort((a, b) => (b.docs[0]?.uploaded_at || "").localeCompare(a.docs[0]?.uploaded_at || ""));
  }, [documents, serviceIndex, applications]);

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="account-page">
      <SEO
        title="My Documents & Submissions | Biz360 Compliance"
        canonical="/account"
        robots="noindex, nofollow"
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <Reveal>
                  <h1 className="text-3xl font-bold tracking-tight text-navy-900 md:text-4xl">My Documents</h1>
                </Reveal>
                <Reveal delay={0.05}>
                  <p className="mt-2 text-slate-600">{user?.name} · {user?.email}</p>
                </Reveal>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/upload"
                  data-testid="account-upload-more"
                  className="inline-flex items-center gap-2 rounded-full bg-gold-500 px-5 py-2.5 text-sm font-semibold text-navy-950 shadow-[0_8px_20px_rgba(212,162,76,0.35)] transition-transform duration-300 hover:scale-105"
                >
                  <UploadCloud className="h-4 w-4" /> Upload Documents
                </Link>
                <button
                  type="button"
                  onClick={logout}
                  data-testid="account-logout"
                  className="inline-flex items-center gap-2 rounded-full border border-navy-900/15 px-5 py-2.5 text-sm font-semibold text-navy-900 transition-colors hover:border-red-300 hover:text-red-600"
                >
                  <LogOut className="h-4 w-4" /> Log Out
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="relative bg-white py-14 md:py-16">
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            {loading ? (
              <p className="py-16 text-center text-slate-500">Loading your documents...</p>
            ) : serviceGroups.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-navy-900/15 bg-[rgb(238,242,250)]/50 py-16 text-center" data-testid="account-empty">
                <p className="text-slate-600">You haven't uploaded any documents yet.</p>
                <Link to="/upload" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gold-600 hover:underline">
                  Upload your first document <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {serviceGroups.map((group) => (
                  <div key={group.slug} className="overflow-hidden rounded-2xl border border-slate-200" data-testid={`account-service-group-${group.slug}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-[rgb(238,242,250)] px-5 py-3.5">
                      <div>
                        <h3 className="text-sm font-bold text-navy-900">{group.title}</h3>
                        <p className="text-xs text-slate-500">{group.category} · {group.docs.length} document{group.docs.length > 1 ? "s" : ""} submitted</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${APP_STATUS[group.status].className}`}
                        title={group.statusNote || undefined}
                        data-testid={`account-app-status-${group.slug}`}
                      >
                        {APP_STATUS[group.status].label}
                      </span>
                      {group.missing.length > 0 && (
                        <Link
                          to={`/upload?service=${encodeURIComponent(group.slug)}&category=${encodeURIComponent(group.category)}&title=${encodeURIComponent(group.title)}`}
                          data-testid={`account-upload-pending-${group.slug}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-gold-500 px-3.5 py-1.5 text-xs font-semibold text-navy-950 transition-transform duration-300 hover:scale-105"
                        >
                          <AlertTriangle className="h-3.5 w-3.5" /> {group.missing.length} pending — Upload now
                        </Link>
                      )}
                      </div>
                    </div>
                    <div>
                      {group.docs.map((doc) => (
                        <DocumentRow
                          key={doc.id}
                          doc={doc}
                          token={token}
                          expanded={expandedId === doc.id}
                          onToggle={() => setExpandedId((prev) => (prev === doc.id ? null : doc.id))}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
